import { useEffect, useRef, useState, useCallback } from "react";
import {
  VaultResponse,
  VgsCollectSubmitResponse,
} from "@/networking/vault.api";
import { getVgsEnvironment } from "@/lib/vgs-utils";
import { useTranslation } from "@/hooks/useTranslation";

interface UseVgsCollectParams {
  onSuccess: (response: VgsCollectSubmitResponse) => void;
  onError: (error: string) => void;
}

export type VgsFieldState = {
  isFocused: boolean;
  isValid: boolean;
  isEmpty: boolean;
};

export type VgsFormState = {
  [key: string]: VgsFieldState;
};

interface VGSCollectForm {
  setRouteId: (routeId: string) => void;
  field: (selector: string, config: Record<string, unknown>) => void;
  submit: (
    path: string,
    options: { headers?: Record<string, string> },
    successCallback: (
      status: number,
      response: VgsCollectSubmitResponse,
    ) => void,
    errorCallback: (errors: Record<string, string[]>) => void,
  ) => void;
  unmount: () => void;
}

interface VGSCollectLibrary {
  create: (
    vaultId: string,
    environment: string,
    stateCallback: (state: Record<string, Partial<VgsFieldState>>) => void,
  ) => VGSCollectForm;
}

const getVGSCollect = (): VGSCollectLibrary | null => {
  if (typeof window !== "undefined") {
    return (
      (window as unknown as { VGSCollect?: VGSCollectLibrary }).VGSCollect ||
      null
    );
  }
  return null;
};

const VGS_IFRAME_LOAD_TIMEOUT_MS = 25_000;

export function useVgsCollect({ onSuccess, onError }: UseVgsCollectParams) {
  const [isFormReady, setIsFormReady] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [iframeLoadError, setIframeLoadError] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [sessionRevision, setSessionRevision] = useState(0);
  const [vaultCredentials, setVaultCredentials] =
    useState<VaultResponse | null>(null);

  const [vgsFieldState, setVgsFieldState] = useState<VgsFormState>({
    card_number: { isFocused: false, isValid: false, isEmpty: true },
    card_exp: { isFocused: false, isValid: false, isEmpty: true },
    card_cvc: { isFocused: false, isValid: false, isEmpty: true },
    card_holder: { isFocused: false, isValid: false, isEmpty: true },
  });

  const { t } = useTranslation();

  const formRef = useRef<VGSCollectForm | null>(null);
  const vaultCredentialsRef = useRef<VaultResponse | null>(null);
  const isInitializingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const iframeLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iframeLoadedRef = useRef(false);
  const scriptFailedRef = useRef(false);

  const resetVgs = useCallback(() => {
    if (formRef.current) {
      try {
        formRef.current.unmount();
      } catch (e) {
        console.warn("VGS unmount error", e);
      }
      formRef.current = null;
    }

    // Reset internal state
    vaultCredentialsRef.current = null;
    setVaultCredentials(null);
    setIsFormReady(false);
    setHasActiveSession(false);
    if (!scriptFailedRef.current) {
      setIframeLoadError(false);
    }
    isInitializedRef.current = false;
    isInitializingRef.current = false;
    iframeLoadedRef.current = false;
    if (iframeLoadTimerRef.current) {
      clearTimeout(iframeLoadTimerRef.current);
      iframeLoadTimerRef.current = null;
    }

    // Reset field state UI
    setVgsFieldState({
      card_number: { isFocused: false, isValid: false, isEmpty: true },
      card_exp: { isFocused: false, isValid: false, isEmpty: true },
      card_cvc: { isFocused: false, isValid: false, isEmpty: true },
      card_holder: { isFocused: false, isValid: false, isEmpty: true },
    });

    setSessionRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // If the script already failed to load, don't retry — show error immediately
    if (scriptFailedRef.current) {
      setIframeLoadError(true);
      return;
    }

    const vgsCollect = getVGSCollect();

    if (vgsCollect) {
      const timeoutId = window.setTimeout(() => {
        setIsScriptLoaded(true);
      }, 0);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    if (isScriptLoaded) return;

    const script = document.createElement("script");
    script.src =
      "https://js.verygoodvault.com/vgs-collect/3.3.0/vgs-collect.js";
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => {
      scriptFailedRef.current = true;
      setIframeLoadError(true);
    };
    document.head.appendChild(script);
  }, [isScriptLoaded, onError]);

  const updateFieldState = useCallback(
    (state: Record<string, Partial<VgsFieldState>>) => {
      if (!state) return;

      // VGS fires state callbacks once iframes mount — clear the load timer
      if (!iframeLoadedRef.current) {
        iframeLoadedRef.current = true;
        if (iframeLoadTimerRef.current) {
          clearTimeout(iframeLoadTimerRef.current);
          iframeLoadTimerRef.current = null;
        }
      }

      setVgsFieldState((prev) => {
        const newState: VgsFormState = { ...prev };
        let hasChanges = false;

        Object.keys(state).forEach((key) => {
          const field = state[key];
          if (!field) return;

          const next: VgsFieldState = {
            isFocused: !!field.isFocused,
            isValid: !!field.isValid,
            isEmpty: !!field.isEmpty,
          };

          if (
            !newState[key] ||
            JSON.stringify(newState[key]) !== JSON.stringify(next)
          ) {
            newState[key] = next;
            hasChanges = true;
          }
        });

        return hasChanges ? newState : prev;
      });
    },
    [],
  );

  const initializeForm = useCallback(
    async (credentials: VaultResponse) => {
      if (
        !isScriptLoaded ||
        isInitializingRef.current ||
        isInitializedRef.current ||
        formRef.current
      ) {
        return false;
      }

      const VGSCollect = getVGSCollect();
      if (!VGSCollect) {
        onError("Payment script not loaded");
        return false;
      }

      try {
        isInitializingRef.current = true;

        vaultCredentialsRef.current = credentials;
        setVaultCredentials(credentials);

        const vgsEnvironment = getVgsEnvironment(credentials.vault.environment);

        const form = VGSCollect.create(
          credentials.vault.vault_id,
          vgsEnvironment,
          (state) => {
            updateFieldState(state);
          },
        );

        if (credentials.vault.route_id) {
          form.setRouteId(credentials.vault.route_id);
        }

        formRef.current = form;
        setHasActiveSession(true);

        setIsFormReady(true);
        await new Promise((resolve) => setTimeout(resolve, 150));

        const css = {
          fontSize: "14px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: "#1f2937",
          paddingTop: "0px",
          paddingBottom: "0px",
          paddingLeft: "0px",
          paddingRight: "0px",
          boxSizing: "border-box",
          lineHeight: "normal",
          height: "100%",
          width: "100%",
          "&::placeholder": {
            color: "#9ca3af",
            opacity: 1,
          },
          "&:focus": {
            outline: "none",
          },
        };

        form.field("#vgs-card-number", {
          type: "card-number",
          name: "card_number",
          placeholder: " ",
          showCardIcon: true,
          css: { ...css, paddingRight: "40px" },
          autoComplete: "cc-number",
          validations: ["required", "validCardNumber"],
        });

        form.field("#vgs-expiration-date", {
          type: "card-expiration-date",
          name: "card_exp",
          yearLength: 2,
          placeholder: " ",
          css,
          autoComplete: "cc-exp",
          validations: ["required", "validCardExpirationDate"],
        });

        form.field("#vgs-cvc", {
          type: "card-security-code",
          name: "card_cvc",
          placeholder: " ",
          css,
          autoComplete: "cc-csc",
          validations: ["required", "validCardSecurityCode"],
        });

        form.field("#vgs-first-name", {
          type: "text",
          name: "card_holder",
          placeholder: " ",
          css,
          autoComplete: "cc-name",
          validations: ["required"],
        });

        isInitializingRef.current = false;
        isInitializedRef.current = true;

        // Start a timer to detect if VGS iframes fail to mount (ad blockers, firewalls, etc.)
        iframeLoadTimerRef.current = setTimeout(() => {
          if (!iframeLoadedRef.current) {
            setIframeLoadError(true);
          }
        }, VGS_IFRAME_LOAD_TIMEOUT_MS);

        return true;
      } catch (error) {
        console.error("Failed to initialize VGS:", error);
        if (formRef.current) {
          try {
            formRef.current.unmount();
          } catch (unmountError) {
            console.warn("VGS unmount error", unmountError);
          }
          formRef.current = null;
        }
        vaultCredentialsRef.current = null;
        setVaultCredentials(null);
        isInitializingRef.current = false;
        isInitializedRef.current = false;
        setIsFormReady(false);
        setHasActiveSession(false);
        onError("Failed to initialize payment form");
        return false;
      }
    },
    [isScriptLoaded, onError, updateFieldState],
  );

  const submitCard = useCallback(async () => {
    if (!formRef.current || !vaultCredentialsRef.current) {
      onError("Form not initialized");
      return;
    }

    formRef.current.submit(
      `/post`,
      {},
      (status: number, response: VgsCollectSubmitResponse) => {
        if (status >= 200 && status < 400) {
          onSuccess(response);
        } else {
          console.error("VGS Error:", response);
          const errorMessage = "Payment failed";
          onError(errorMessage);
        }
      },
      (errors: Record<string, string[]>) => {
        console.error("VGS Validation errors:", errors);

        // Map VGS field names to user-friendly names
        const fieldNameMap: Record<string, string> = {
          card_number: t("card_number"),
          card_exp: t("expiration_date"),
          card_cvc: t("cvc"),
          card_holder: t("cardholder_name"),
        };

        // Map VGS field names to "is required" messages
        const fieldRequiredMap: Record<string, string> = {
          card_number: t("card_number_is_required"),
          card_exp: t("expiration_date_is_required"),
          card_cvc: t("cvv_is_required"),
          card_holder: t("cardholder_name_is_required"),
        };

        let errorMsg = t("please_fix_payment_information_before_proceeding");

        if (errors && typeof errors === "object") {
          const errorMessages: string[] = [];

          Object.entries(errors).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages.length > 0) {
              // VGS returns error messages in array
              messages.forEach((msg) => {
                if (msg && typeof msg === "string") {
                  errorMessages.push(msg);
                }
              });
            } else if (typeof messages === "string" && messages) {
              // Single error message
              errorMessages.push(messages);
            } else {
              // No specific message, use field-specific "is required" or fallback
              const requiredMsg = fieldRequiredMap[field];
              const friendlyFieldName =
                fieldNameMap[field] || field.replace(/_/g, " ");
              errorMessages.push(requiredMsg || friendlyFieldName);
            }
          });

          if (errorMessages.length > 0) {
            errorMsg = errorMessages.join(", ");
          }
        }

        onError(errorMsg);
      },
    );
  }, [onSuccess, onError, t]);

  useEffect(() => {
    return () => {
      if (formRef.current) {
        formRef.current = null;
      }
      if (isInitializedRef.current) {
        isInitializedRef.current = false;
      }
      if (iframeLoadTimerRef.current) {
        clearTimeout(iframeLoadTimerRef.current);
        iframeLoadTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      resetVgs();
    };
  }, [resetVgs]);

  return {
    isFormReady,
    iframeLoadError,
    hasActiveSession,
    initializeForm,
    sessionRevision,
    submitCard,
    vgsFieldState,
    vaultCredentials,
    resetVgs,
  };
}
